import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Vote, ShieldCheck, Sparkles, Radio, Trophy, Users, Plus, X, CheckCircle2,
  Lock, Clock, BadgeCheck, Share2,
} from "lucide-react";

const POLL_MS = 4000;

function StatPill({ icon: Icon, label, value, tone = "default" }) {
  const toneCls = tone === "gold"
    ? "border-gold bg-gold/10 text-gold"
    : tone === "success"
      ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
      : "border-border bg-accent/40 text-foreground/85";
  return (
    <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium border ${toneCls}`}>
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
      <span className="font-mono text-foreground/95">{value}</span>
    </div>
  );
}

function IdentityBadge({ user }) {
  return (
    <div className="hoa-card royal-ring p-4 flex items-center gap-3" data-testid="live-vote-identity">
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center flex-shrink-0">
        <ShieldCheck className="w-5 h-5 text-primary-foreground" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-gold-soft">Verified identity</div>
        <div className="font-semibold text-foreground truncate" data-testid="voter-name">{user?.name}</div>
        <div className="text-xs text-muted-foreground truncate">{user?.email} · {({ owner: "Homeowner", board: "Board Director", admin: "Administrator" }[user?.role]) || "Member"}</div>
      </div>
      <BadgeCheck className="w-5 h-5 text-gold ml-auto flex-shrink-0" />
    </div>
  );
}

function LiveTallyRow({ option, count, total, pct, leading, mine }) {
  return (
    <div
      className={`relative overflow-hidden rounded-lg border transition-colors ${leading ? "border-gold" : "border-border"}`}
      data-testid={`tally-row-${option.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div
        className={`absolute inset-y-0 left-0 transition-all duration-700 ${leading ? "bg-gold/25" : "bg-primary/15"}`}
        style={{ width: `${pct}%` }}
      />
      <div className="relative flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          {mine && <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />}
          {leading && !mine && <Trophy className="w-4 h-4 text-gold flex-shrink-0" />}
          <span className="font-medium text-foreground/95 truncate">{option}</span>
        </div>
        <div className="text-right flex-shrink-0 ml-3">
          <div className="font-mono text-lg text-gold" data-testid={`tally-pct`}>{pct}%</div>
          <div className="font-mono text-[10px] text-muted-foreground">{count} of {total}</div>
        </div>
      </div>
    </div>
  );
}

function ElectionCard({ election, isBoard, onVoted, onClosed }) {
  const [casting, setCasting] = useState(null);
  const [receipt, setReceipt] = useState(election.my_verification_code || null);
  const votedIdx = election.my_vote;
  const hasVoted = Number.isInteger(votedIdx);

  const cast = async (idx) => {
    if (!election.is_open || casting !== null) return;
    setCasting(idx);
    try {
      const { data } = await api.post(`/polls/${election.id}/vote`, { option_index: idx });
      setReceipt(data.verification_code);
      toast.success(`Ballot cast · receipt #${data.verification_code}`);
      onVoted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vote failed");
    } finally {
      setCasting(null);
    }
  };

  const closeElection = async () => {
    try {
      await api.post(`/polls/${election.id}/close`);
      toast.success("Election closed");
      onClosed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not close");
    }
  };

  return (
    <div className="hoa-card royal-ring p-6 md:p-8 relative overflow-hidden" data-testid={`election-${election.id}`}>
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/25 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
            <Radio className={`w-3.5 h-3.5 ${election.is_open ? "text-emerald-400 animate-pulse" : "text-muted-foreground"}`} />
            <span className={election.is_open ? "text-emerald-400" : "text-muted-foreground"}>
              {election.is_open ? "Live · votes counting" : (election.closed ? "Closed" : "Not yet open")}
            </span>
          </div>
          {isBoard && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full h-8 border-gold text-gold hover:bg-gold/10"
              onClick={() => {
                const url = `${window.location.origin}/vote/broadcast/${election.id}`;
                navigator.clipboard?.writeText(url).catch(() => {});
                toast.success("Broadcast link copied — paste it into email, a QR code, or a lobby TV.");
              }}
              data-testid={`copy-broadcast-${election.id}`}
            >
              <Share2 className="w-3.5 h-3.5 mr-1" /> Copy broadcast link
            </Button>
          )}
          {isBoard && election.is_open && (
            <Button size="sm" variant="outline" className="rounded-full h-8" onClick={closeElection} data-testid={`close-election-${election.id}`}>
              <Lock className="w-3.5 h-3.5 mr-1" /> Close election
            </Button>
          )}
        </div>
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2 text-foreground">{election.question}</h2>
        {election.description && (
          <p className="text-lg text-muted-foreground mb-5 leading-relaxed">{election.description}</p>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <StatPill icon={Users} label="Votes cast" value={`${election.total_votes} / ${election.owner_count}`} />
          <StatPill
            icon={CheckCircle2}
            label={election.quorum_reached ? "Quorum reached" : `Quorum ${election.quorum_pct}%`}
            value={`${election.total_votes}/${election.quorum_needed}`}
            tone={election.quorum_reached ? "success" : "default"}
          />
          {hasVoted && receipt && (
            <StatPill icon={BadgeCheck} label="Your receipt" value={`#${receipt}`} tone="gold" />
          )}
          {election.closes_at && (
            <StatPill icon={Clock} label="Closes" value={new Date(election.closes_at).toLocaleString()} />
          )}
        </div>

        <div className="space-y-2.5">
          {election.options.map((opt, i) => {
            const count = election.tally[i] || 0;
            const total = election.total_votes || 0;
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            const leading = election.leading_option_index === i && total > 0;
            const mine = votedIdx === i;
            if (!hasVoted && election.is_open) {
              return (
                <button
                  key={`${election.id}-cast-${i}`}
                  onClick={() => cast(i)}
                  disabled={casting !== null}
                  className="w-full text-left rounded-lg border border-border hover:border-gold hover:bg-accent/40 transition-colors px-4 py-3.5 flex items-center justify-between disabled:opacity-60"
                  data-testid={`cast-vote-${election.id}-${i}`}
                >
                  <span className="font-medium text-foreground">{opt}</span>
                  <span className="text-xs text-gold font-semibold flex items-center gap-1">
                    {casting === i ? "Casting…" : <>Cast ballot <Vote className="w-3.5 h-3.5" /></>}
                  </span>
                </button>
              );
            }
            return (
              <LiveTallyRow
                key={`${election.id}-tally-${i}`}
                option={opt}
                count={count}
                total={total}
                pct={pct}
                leading={leading}
                mine={mine}
              />
            );
          })}
        </div>

        {hasVoted && receipt && (
          <div className="mt-6 p-4 rounded-xl bg-gold/10 border border-gold/40 flex flex-col md:flex-row md:items-center gap-3" data-testid={`vote-receipt-${election.id}`}>
            <Sparkles className="w-5 h-5 text-gold flex-shrink-0" />
            <p className="text-sm text-foreground/90 flex-1">
              <span className="font-semibold text-gold">Ballot verified.</span>{" "}
              Your vote for <b>{election.options[votedIdx]}</b> is on record with receipt <span className="font-mono text-gold">#{receipt}</span>. This page updates every few seconds — watch the results roll in.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateElectionDialog({ open, setOpen, onCreated }) {
  const [q, setQ] = useState("");
  const [desc, setDesc] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [quorum, setQuorum] = useState(50);
  const [closesAt, setClosesAt] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (clean.length < 2) return toast.error("Add at least 2 options");
    setSaving(true);
    try {
      await api.post("/polls", {
        question: q,
        description: desc || null,
        options: clean,
        is_election: true,
        quorum_pct: Number(quorum) || 50,
        closes_at: closesAt ? new Date(closesAt).toISOString() : null,
      });
      toast.success("Election opened");
      setQ(""); setDesc(""); setOptions(["", ""]); setQuorum(50); setClosesAt("");
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open election");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110 glow-gold" data-testid="new-election-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Open new election
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Open a new election</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="election-form">
          <div>
            <Label>Question</Label>
            <Input required value={q} onChange={(e) => setQ(e.target.value)} className="mt-1.5 h-11" placeholder="Elect the 2026 Board Treasurer" data-testid="election-question" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} className="mt-1.5" placeholder="Verified vote — one ballot per owner. Real-time results below." data-testid="election-description" />
          </div>
          <div>
            <Label>Ballot options</Label>
            <div className="space-y-2 mt-1.5">
              {options.map((o, i) => (
                <div key={`opt-${i}`} className="flex gap-2">
                  <Input
                    value={o}
                    onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))}
                    className="h-11"
                    placeholder={`Option ${i + 1}`}
                    data-testid={`election-option-${i}`}
                  />
                  {options.length > 2 && (
                    <button type="button" onClick={() => setOptions(options.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive" data-testid={`election-remove-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setOptions([...options, ""])} data-testid="election-add-option">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add option
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Quorum %</Label>
              <Input type="number" min="0" max="100" value={quorum} onChange={(e) => setQuorum(e.target.value)} className="mt-1.5 h-11" data-testid="election-quorum" />
            </div>
            <div>
              <Label>Closes at</Label>
              <Input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} className="mt-1.5 h-11" data-testid="election-closes-at" />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="rounded-full" data-testid="election-submit">
              {saving ? "Opening…" : "Open election"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function LiveVote() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [data, setData] = useState({ elections: [], owner_count: 0, server_time: null });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/elections/live");
      setData(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("live-vote poll swallowed:", err?.message || err);
      // silent — auto-refresh keeps trying
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, POLL_MS);
    return () => clearInterval(timer.current);
  }, []);

  const activeCount = data.elections.filter((e) => e.is_open).length;
  const closedCount = data.elections.length - activeCount;

  return (
    <div className="space-y-6" data-testid="live-vote-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
            <Vote className="w-4 h-4" /> Live Vote · Election Day
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Cast your ballot. Watch results live.</h1>
          <p className="text-lg text-foreground/75 mt-2 max-w-2xl">
            Every ballot is verified against your account and receipted. Tallies refresh every {POLL_MS / 1000} seconds — no reloads needed.
          </p>
        </div>
        {isBoard && <CreateElectionDialog open={dialog} setOpen={setDialog} onCreated={load} />}
      </div>

      <IdentityBadge user={user} />

      <div className="flex flex-wrap gap-2">
        <StatPill icon={Radio} label="Active elections" value={activeCount} tone={activeCount ? "success" : "default"} />
        <StatPill icon={Lock} label="Closed" value={closedCount} />
        <StatPill icon={Users} label="Eligible voters" value={data.owner_count} />
      </div>

      {loading ? (
        <div className="hoa-card p-12 text-center text-muted-foreground" data-testid="live-vote-loading">Loading live tally…</div>
      ) : data.elections.length === 0 ? (
        <div className="hoa-card p-12 text-center" data-testid="live-vote-empty">
          <Vote className="w-8 h-8 text-gold mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">No elections open yet.</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {isBoard
              ? "Click 'Open new election' to launch a verified board vote or bylaw amendment. Owners will see it here instantly."
              : "The board hasn't opened an election. Once they do, it will appear here in real time."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {data.elections.map((e) => (
            <ElectionCard key={e.id} election={e} isBoard={isBoard} onVoted={load} onClosed={load} />
          ))}
        </div>
      )}
    </div>
  );
}
